import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { money, fmtTime } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Check, X, ClipboardCheck } from "lucide-react";

export default function Approvals() {
  const qc = useQueryClient();
  const { data: orders = [] } = useQuery({
    queryKey: ["approvals"],
    queryFn: async () => (await api.get("/orders?awaiting_approval=true")).data,
    refetchInterval: 4000,
  });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const sym = settings?.currency_symbol || "₹";

  const act = async (o, kind) => {
    try {
      await api.post(`/orders/${o.id}/${kind}`);
      toast.success(kind === "approve" ? `${o.order_number} sent to kitchen` : `${o.order_number} rejected`);
      qc.invalidateQueries({ queryKey: ["approvals"] });
      qc.invalidateQueries({ queryKey: ["kitchen"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8" data-testid="approvals">
      <PageHeader title="Waiter Approvals" subtitle="Approve waiter tickets before they reach the kitchen" />
      {settings?.kitchen_order_flow !== "approve" && (
        <div className="kul-card p-4 mb-4 text-sm text-neutral-500">
          Kitchen routing is set to <b>Waiter → Kitchen</b>. Switch to manager approval in Settings if you want tickets to pause here.
        </div>
      )}
      <div className="space-y-3 max-w-3xl">
        {orders.map((o) => (
          <div key={o.id} className="kul-card p-5" data-testid={`approval-${o.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-display font-700 text-lg flex items-center gap-2">
                  <ClipboardCheck className="w-4 h-4 text-[var(--kul-primary)]" /> {o.order_number}
                </div>
                <div className="text-xs text-neutral-500 mt-1">
                  Waiter {o.created_by}{o.table_name ? ` · Table ${o.table_name}` : ""} · {fmtTime(o.created_at)} · {money(o.total, sym)}
                </div>
                <ul className="mt-3 text-sm space-y-1">
                  {o.items.map((it, i) => <li key={i}>{it.qty}× {it.name}</li>)}
                </ul>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <Button onClick={() => act(o, "approve")} className="bg-[var(--kul-primary)] text-white" data-testid={`approve-${o.id}`}>
                  <Check className="w-4 h-4 mr-1" /> Approve
                </Button>
                <Button variant="outline" onClick={() => act(o, "reject")} data-testid={`reject-${o.id}`}>
                  <X className="w-4 h-4 mr-1" /> Reject
                </Button>
              </div>
            </div>
          </div>
        ))}
        {!orders.length && <div className="text-neutral-400 py-16 text-center">No waiter tickets waiting.</div>}
      </div>
    </div>
  );
}
